import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

const items = [
  {
    name: "Rohini Aneesh",
    location: "Koottanad",
    stars: 5,
    quote:
      "I had a great experience with postnatal care services in Kakkanat Ayurveda. The staff were caring, supportive, and professional throughout.",
    readMore: true,
  },
  {
    name: "Anju KS",
    location: "Thrissur",
    stars: 5,
    quote:
      "It was a really great experience visiting here. The food service and treatment were all top class. It's definitely a place I'd love to visit again!",
  },
  {
    name: "Aiswarya",
    location: "Shoranur",
    stars: 5,
    quote: "Well satisfied.",
  },
];

const GoogleG = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.7 0-14.3 4.4-17.7 10.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2C41.4 35.6 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

export const Testimonials = () => {
  return (
    <section
      id="testimonials"
      className="py-16 md:py-36 bg-sand/40 bg-grain"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-5xl"
        >
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              From the mothers we've held
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
              Stories carried home.
            </h2>
          </div>

          <a
            href="https://share.google/cgbtNduZ8gm73QyOH"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 self-start md:self-end text-xs text-charcoal/65 bg-white border border-sage/20 rounded-full pl-3 pr-4 py-2 hover:border-sage hover:text-charcoal transition-colors"
            data-testid="google-attribution"
          >
            <GoogleG size={14} />
            <span>Sourced from Google Reviews</span>
          </a>
        </motion.div>

        {/* Mobile: horizontal swipeable carousel */}
        <div
          className="md:hidden mt-12 -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2"
          style={{ scrollbarWidth: "none" }}
          data-testid="testimonials-mobile-scroll"
        >
          {items.map((t, i) => (
            <TestimonialCard
              key={`m-${t.name}`}
              t={t}
              i={i}
              className="shrink-0 w-[86%] snap-start"
            />
          ))}
          <div className="shrink-0 w-2" aria-hidden />
        </div>
        <div className="md:hidden mt-3 text-center text-xs text-charcoal/45 italic">
          Swipe through reviews →
        </div>

        {/* Desktop: grid */}
        <div className="hidden md:grid mt-14 md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((t, i) => (
            <TestimonialCard key={t.name} t={t} i={i} />
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <a
            href="https://share.google/cgbtNduZ8gm73QyOH"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-sage/40 text-charcoal px-6 py-3 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
            data-testid="read-all-google"
          >
            <GoogleG size={15} />
            Read all reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

const TestimonialCard = ({ t, i, className = "" }) => (
  <motion.figure
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{
      duration: 0.7,
      delay: i * 0.08,
      ease: [0.22, 1, 0.36, 1],
    }}
    className={`bg-white border border-sage/15 rounded-3xl p-7 md:p-9 flex flex-col ${className}`}
    data-testid={`testimonial-card-${i + 1}`}
  >
    <div className="flex items-center justify-between">
      <Quote size={26} strokeWidth={1.25} className="text-sage" />
      <GoogleG size={16} />
    </div>
    <blockquote className="mt-5 font-serif text-lg md:text-[22px] leading-snug text-charcoal italic">
      “{t.quote}”
    </blockquote>
    {t.readMore && (
      <a
        href="https://share.google/cgbtNduZ8gm73QyOH"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sage-700 hover:text-charcoal transition-colors"
        data-testid={`testimonial-readmore-${i + 1}`}
      >
        Read full review →
      </a>
    )}
    <figcaption className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-sage/15">
      <div className="flex items-center gap-1 text-sage">
        {Array.from({ length: t.stars }).map((_, k) => (
          <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
        ))}
      </div>
      <div className="mt-3 font-medium text-charcoal">{t.name}</div>
      <div className="text-xs text-charcoal/55 mt-0.5">{t.location}</div>
    </figcaption>
  </motion.figure>
);
